import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, AlertCircle, CheckCircle, Download, FileText, Loader2, Plus, RefreshCw, Send, ShieldCheck, Trash2, Upload, X, XCircle,
} from 'lucide-react';
import { adminContractsAPI, adminLookupAPI } from '../../services/api';
import SignatureCapture from '../../components/common/SignatureCapture';
import { formatDateTime } from '../../utils/format';
import { useAuthStore } from '../../context/authStore';
import { StatusBadge, KIND_LABELS } from './AdminContractsPage';
import ContractAgreementCard from '../../components/admin/ContractAgreementCard';

/**
 * One contract, from upload to sealed copy.
 *
 * A draft is edited freely: the document, who signs and in what order, the
 * company and the ConnectWise agreement it belongs to. Sending freezes it —
 * the server takes the document's SHA-256 and from then on the database
 * refuses any change, so a correction is a new version (the old one is
 * voided and its signers told). When every client signer has signed, it
 * waits here for a countersignature; that seals it, emails everyone the
 * signed copy and attaches it in ConnectWise.
 *
 * The signers' IP addresses and browsers are shown here and nowhere a client
 * can see.
 */

const errorOf = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const card = 'rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 p-6 space-y-4';

const EVENT_LABELS = {
  created: 'Created', edited: 'Edited', sent: 'Sent for signature', link_sent: 'Signing link emailed', reminded: 'Reminder emailed',
  resent: 'Link re-sent', viewed: 'Opened', code_sent: 'Code emailed', code_failed: 'Wrong code entered', verified: 'Identity confirmed',
  consented: 'Agreed to sign electronically', signed: 'Signed', declined: 'Declined', awaiting_countersignature: 'Ready to countersign',
  countersigned: 'Countersigned', completed: 'Sealed', copy_emailed: 'Signed copy emailed', email_failed: 'Email failed',
  filed_cw: 'Filed in ConnectWise', file_cw_failed: 'ConnectWise filing failed', voided: 'Voided', discarded: 'Draft discarded',
  verified_integrity: 'Integrity checked', opened_from_portal: 'Opened from the portal',
  agreement_created: 'Agreement created in ConnectWise', additions_added: 'Lines added to the agreement',
  agreement_failed: 'ConnectWise did not create the agreement', agreement_parent_skipped: 'Not put under the MSA parent',
  released: 'Released for signature', chose: 'Choice made',
};

function emailOf(contact) {
  const items = contact?.communicationItems || [];
  return (items.find((i) => i.communicationType === 'Email' && i.defaultFlag) || items.find((i) => i.communicationType === 'Email'))?.value || '';
}

function Banner({ tone = 'error', children }) {
  const cls = tone === 'ok'
    ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
    : tone === 'warn' ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
      : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200';
  return <div className={`flex gap-2 rounded-lg p-3 text-sm ${cls}`}><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><div>{children}</div></div>;
}

/** The draft form: used for a new contract and for editing a draft. */
function DraftForm({ contract, onSaved }) {
  const isNew = !contract;
  const [company, setCompany] = useState(contract ? { id: contract.cw_company_id, name: contract.company_name } : null);
  const [companyQuery, setCompanyQuery] = useState('');
  const [title, setTitle] = useState(contract?.title || '');
  const [kind, setKind] = useState(contract?.kind || 'msa');
  const [message, setMessage] = useState(contract?.message || '');
  const [agreementId, setAgreementId] = useState(contract?.cw_agreement_id || '');
  const [waitsFor, setWaitsFor] = useState(contract?.parent_contract_id || '');
  const [countersign, setCountersign] = useState(contract ? contract.countersign_required : true);
  const [fileUnderMsa, setFileUnderMsa] = useState(contract ? contract.file_under_msa !== false : true);
  const [days, setDays] = useState(contract?.origin_detail?.expiresInDays || 30);
  const [signers, setSigners] = useState(contract?.signers?.filter((s) => s.role === 'signer').map((s) => ({
    name: s.name, email: s.email, title: s.title || '', cwContactId: s.cwContactId, signOrder: s.signOrder,
  })) || []);
  const [choices, setChoices] = useState((contract?.choices || []).map((c) => ({ key: c.key, label: c.label, options: c.options.join('\n'), chooser: c.chooser })));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [contactQuery, setContactQuery] = useState('');

  const companies = useQuery({
    queryKey: ['cw-company-search', companyQuery],
    queryFn: () => adminLookupAPI.companies(companyQuery).then((r) => r.data.companies),
    enabled: !company && companyQuery.length >= 2,
  });
  const agreements = useQuery({
    queryKey: ['cw-agreements', company?.id],
    queryFn: () => adminLookupAPI.companyAgreements(company.id).then((r) => r.data.items || []),
    enabled: !!company?.id,
  });
  // Contracts this one can be signed after: the client's open ones (not itself).
  const openContracts = useQuery({
    queryKey: ['admin-contracts-open', company?.id],
    queryFn: () => adminContractsAPI.list({ companyId: company.id, status: 'open' }).then((r) => r.data.contracts.filter((c) => c.id !== contract?.id && c.kind !== 'quote')),
    enabled: !!company?.id,
  });
  const contacts = useQuery({
    queryKey: ['cw-contacts', company?.id, contactQuery],
    queryFn: () => adminLookupAPI.contactSearch(company.id, contactQuery).then((r) => r.data.items || []),
    enabled: !!company?.id,
  });

  const addContact = (c) => {
    const email = emailOf(c);
    if (!email || signers.some((s) => s.email.toLowerCase() === email.toLowerCase())) return;
    setSigners([...signers, { name: [c.firstName, c.lastName].filter(Boolean).join(' '), email, title: c.title || '', cwContactId: c.id, signOrder: 1 }]);
  };
  const updateSigner = (i, patch) => setSigners(signers.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const fields = {
        companyId: company?.id, companyName: company?.name, title, kind, message, countersignRequired: countersign, fileUnderMsa,
        cwAgreementId: agreementId ? Number(agreementId) : null, expiresInDays: Number(days),
        parentContractId: waitsFor || null,
        signers: signers.map((s) => ({ ...s, signOrder: Number(s.signOrder) || 1 })),
        choices: choices.map((c) => ({ ...(c.key ? { key: c.key } : {}), label: c.label, chooser: c.chooser || 'client', options: c.options.split('\n').map((o) => o.trim()).filter(Boolean) })),
      };
      const res = isNew ? await adminContractsAPI.create(fields, file) : await adminContractsAPI.update(contract.id, fields, file);
      setFile(null);
      onSaved(res.data);
    } catch (e) {
      setError(errorOf(e, 'Could not save the contract.'));
    } finally {
      setBusy(false);
    }
  };

  const label = 'block text-sm text-neutral-600 dark:text-neutral-300 mb-1';
  return (
    <div className={card}>
      <h2 className="text-lg font-semibold dark:text-white">{isNew ? 'New contract' : 'Draft'}</h2>
      {error && <Banner>{error}</Banner>}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <span className={label}>Company</span>
          {company ? (
            <div className="flex items-center gap-2">
              <span className="font-medium dark:text-white">{company.name}</span>
              {isNew && <button onClick={() => { setCompany(null); setSigners([]); setAgreementId(''); }} className="text-xs text-primary-600 hover:underline">Change</button>}
            </div>
          ) : (
            <div className="relative">
              <input value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)} placeholder="Search ConnectWise companies" className="input w-full" />
              {companies.data?.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-neutral-200 bg-white shadow dark:border-neutral-700 dark:bg-neutral-800">
                  {companies.data.map((c) => (
                    <li key={c.id}><button onClick={() => setCompany({ id: c.id, name: c.name })} className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 dark:text-neutral-200">{c.name}</button></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <label>
          <span className={label}>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="input w-full" placeholder="Master Services Agreement" />
        </label>
        <label>
          <span className={label}>Type</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input w-full">
            {['msa', 'sow', 'order', 'nda', 'policy', 'other'].map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label>
          <span className={label}>ConnectWise agreement (the signed copy is filed there)</span>
          <select value={agreementId} onChange={(e) => setAgreementId(e.target.value)} className="input w-full" disabled={!company}>
            <option value="">{waitsFor && fileUnderMsa ? 'None — file it under the MSA' : 'None — file it on the company'}</option>
            {agreements.data?.map((a) => <option key={a.id} value={a.id}>{a.name}{a.type?.name ? ` (${a.type.name})` : ''}</option>)}
          </select>
        </label>
        <label className="md:col-span-2">
          <span className={label}>Signed after (optional)</span>
          <select value={waitsFor} onChange={(e) => setWaitsFor(e.target.value)} className="input w-full" disabled={!company}>
            <option value="">Nothing — it goes out when it is sent</option>
            {openContracts.data?.map((c) => <option key={c.id} value={c.id}>{c.title} ({KIND_LABELS[c.kind] || c.kind}, {c.status})</option>)}
            {waitsFor && !openContracts.data?.some((c) => c.id === waitsFor) && <option value={waitsFor}>The contract it waits for</option>}
          </select>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            For an Order that needs its MSA first: it is locked and sent with the MSA, and unlocks when the client has signed the MSA, so they can sign both in one sitting. You countersign the package at the end.
          </span>
          {waitsFor && !agreementId && (
            <span className="flex items-center gap-2 text-sm dark:text-neutral-200 mt-2">
              <input type="checkbox" checked={fileUnderMsa} onChange={(e) => setFileUnderMsa(e.target.checked)} />
              File the signed copy under the MSA ahead of it
            </span>
          )}
          {waitsFor && !agreementId && (
            <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              The usual choice. For an exception, choose an agreement above, or untick this to file it on its opportunity or the company. If no MSA is ahead of it, this does nothing.
            </span>
          )}
        </label>
      </div>

      <div>
        <span className={label}>Document (PDF, up to 20 MB)</span>
        <div className="flex flex-wrap items-center gap-3">
          <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer">
            <Upload className="w-4 h-4" /> {file ? 'Choose a different file' : contract?.original_filename ? 'Replace the PDF' : 'Choose a PDF'}
            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
          <span className="text-sm text-neutral-600 dark:text-neutral-300">{file?.name || contract?.original_filename || 'No file yet'}</span>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
          To put signatures in the document, type <code>{'{{signature:client}}'}</code>, <code>{'{{date:client}}'}</code> and <code>{'{{initials:client}}'}</code> where the client signs
          (<code>client2</code>, <code>client3</code> for further signers) and <code>{'{{signature:msp}}'}</code> where you countersign, then export to PDF. Without them, signatures go on a page of their own at the end.
          A choice below is printed where the document says <code>{'{{choice:key}}'}</code>.
        </p>
      </div>

      <div>
        <span className={label}>Who signs</span>
        <div className="space-y-2">
          {signers.map((s, i) => (
            <div key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_6rem_auto] items-center">
              <input value={s.name} onChange={(e) => updateSigner(i, { name: e.target.value })} placeholder="Name" className="input" />
              <input value={s.email} onChange={(e) => updateSigner(i, { email: e.target.value })} placeholder="Email" className="input" />
              <input value={s.title} onChange={(e) => updateSigner(i, { title: e.target.value })} placeholder="Title (optional)" className="input" />
              <input type="number" min={1} value={s.signOrder} onChange={(e) => updateSigner(i, { signOrder: e.target.value })} title="Signing order: 1 signs first" className="input" />
              <button onClick={() => setSigners(signers.filter((_, j) => j !== i))} className="p-2 text-neutral-400 hover:text-red-600" aria-label="Remove signer"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2">
            {company && (
              <>
                <input value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} placeholder="Find a contact" className="input w-56" />
                {contacts.data?.slice(0, 6).map((c) => (
                  <button key={c.id} disabled={!emailOf(c)} onClick={() => addContact(c)} title={emailOf(c) || 'No email address in ConnectWise'}
                    className="rounded-full border border-neutral-300 px-2.5 py-1 text-xs hover:bg-neutral-50 disabled:opacity-40 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700">
                    + {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                  </button>
                ))}
              </>
            )}
            <button onClick={() => setSigners([...signers, { name: '', email: '', title: '', signOrder: 1 }])} className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Someone else
            </button>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Signers with the same number sign in any order; a higher number waits for the lower ones.</p>
        </div>
      </div>

      <div>
        <span className={label}>Choices the client makes as they sign (optional)</span>
        <div className="space-y-3">
          {choices.map((c, i) => {
            const key = c.key || c.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
            const set = (patch) => setChoices(choices.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            return (
              <div key={i} className="grid gap-2 md:grid-cols-[1fr_1fr_12rem_auto] items-start rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
                <div>
                  <input value={c.label} onChange={(e) => set({ label: e.target.value })} placeholder="What they choose, e.g. Initial term" className="input w-full" />
                  {key && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">In the document: <code>{`{{choice:${key}}}`}</code></p>}
                </div>
                <textarea value={c.options} onChange={(e) => set({ options: e.target.value })} rows={3} placeholder={'One option per line\n12 months\n24 months'} className="input w-full" />
                <select value={c.chooser || 'client'} onChange={(e) => set({ chooser: e.target.value })} className="input w-full" title="Who chooses; they must be among the first to sign">
                  {(signers.length ? signers : [{ name: 'First signer' }]).map((sg, k) => {
                    const marker = k === 0 ? 'client' : `client${k + 1}`;
                    return <option key={marker} value={marker}>Chosen by {sg.name || `signer ${k + 1}`}</option>;
                  })}
                </select>
                <button onClick={() => setChoices(choices.filter((_, j) => j !== i))} className="p-2 text-neutral-400 hover:text-red-600" aria-label="Remove choice"><X className="w-4 h-4" /></button>
              </div>
            );
          })}
          <button onClick={() => setChoices([...choices, { label: '', options: '', chooser: 'client' }])} className="text-sm text-primary-600 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> Add a choice
          </button>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">The signer picks one option on the signing page; it is printed in the signed copy and on the certificate. Nobody else signs until it is chosen, so whoever chooses must be among the first to sign.</p>
        </div>
      </div>


      <div className="grid gap-4 md:grid-cols-2">
        <label className="md:col-span-2">
          <span className={label}>Message in the email (optional)</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="input w-full" />
        </label>
        <label>
          <span className={label}>Link works for (days)</span>
          <input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="input w-32" />
        </label>
        <label className="flex items-center gap-2 text-sm dark:text-neutral-200 self-end">
          <input type="checkbox" checked={countersign} onChange={(e) => setCountersign(e.target.checked)} />
          We countersign after the client (recommended for an MSA)
        </label>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={busy || !company || !title.trim() || (isNew && !file)} className="btn-primary inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isNew ? 'Save draft' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

export default function AdminContractDetailPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [voidReason, setVoidReason] = useState('');
  const [showVoid, setShowVoid] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signTitle, setSignTitle] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const onSignature = useCallback((s) => setSignature(s), []);

  const { data: contract, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['admin-contract', id],
    queryFn: () => adminContractsAPI.get(id).then((r) => r.data),
    enabled: !isNew,
  });
  const markers = useQuery({
    queryKey: ['admin-contract-markers', id, contract?.original_sha256],
    queryFn: () => adminContractsAPI.markers(id).then((r) => r.data),
    enabled: !isNew && !!contract?.original_sha256,
  });

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const run = async (name, fn, success = '') => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      const r = await fn();
      if (success) setNotice(success);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['admin-contracts'] });
      return r;
    } catch (e) {
      setError(errorOf(e, 'That did not work.'));
      await refetch();
      return null;
    } finally {
      setBusy('');
    }
  };

  const openPdf = async (kind) => {
    const r = kind === 'signed' ? await adminContractsAPI.signed(id) : await adminContractsAPI.original(id);
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (kind === 'signed') {
      const a = document.createElement('a');
      a.href = url;
      a.download = `${contract.title} - signed.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else {
      setPreview(url);
    }
  };

  const signerSummary = useMemo(() => (contract?.signers || []).filter((s) => s.role === 'signer'), [contract]);
  const placed = useMemo(() => {
    const m = markers.data?.markers || [];
    return (who) => m.some((x) => x.kind === 'signature' && x.who === who);
  }, [markers.data]);

  if (isNew) {
    return (
      <div className="space-y-6">
        <Link to="/admin/contracts" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"><ArrowLeft className="w-4 h-4" /> Contracts</Link>
        <DraftForm onSaved={(c) => navigate(`/admin/contracts/${c.id}`, { replace: true })} />
      </div>
    );
  }
  if (isLoading) return <div className="flex items-center gap-2 text-neutral-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (loadError || !contract) return <div className="p-6"><Banner>{errorOf(loadError, 'Contract not found.')}</Banner></div>;

  const isDraft = contract.status === 'draft';
  return (
    <div className="space-y-6">
      <Link to="/admin/contracts" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"><ArrowLeft className="w-4 h-4" /> Contracts</Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white break-words">{contract.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
            <StatusBadge status={contract.status} expired={contract.expired} held={contract.held_until_parent} />
            <span>{KIND_LABELS[contract.kind]}</span>
            <span>· {contract.company_name || `Company #${contract.cw_company_id}`}</span>
            {contract.version > 1 && <span>· version {contract.version}</span>}
            {contract.origin === 'api' && <span className="rounded bg-amber-100 px-1.5 text-amber-800">Drafted via API{contract.origin_detail?.apiKeyName ? ` (${contract.origin_detail.apiKeyName})` : ''} — review before sending</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {contract.original_filename && <button onClick={() => openPdf('original')} className="btn-secondary inline-flex items-center gap-2"><FileText className="w-4 h-4" /> View document</button>}
          {contract.status === 'completed' && <button onClick={() => openPdf('signed')} className="btn-primary inline-flex items-center gap-2"><Download className="w-4 h-4" /> Signed copy</button>}
        </div>
      </div>

      {error && <Banner>{error}</Banner>}
      {notice && <Banner tone="ok">{notice}</Banner>}

      {isDraft && <DraftForm contract={contract} onSaved={() => { refetch(); setNotice('Saved.'); }} />}

      {isDraft && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Send for signature</h2>
          {markers.data && (
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {signerSummary.map((s) => `${s.name}: ${placed(s.marker) ? 'signs where the document says' : 'signs on a page added at the end'}`).join(' · ')}
              {contract.countersign_required ? ` · Your countersignature: ${placed('msp') ? 'where the document says' : 'on the added page'}` : ''}
            </p>
          )}
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Sending locks the document: its fingerprint is recorded and it can no longer be edited. Each signer gets an email with their own link and confirms it is them with a code.
          </p>
          {contract.waitsFor?.status === 'draft' && (
            <Banner tone="warn">
              This is signed after <Link to={`/admin/contracts/${contract.waitsFor.id}`} className="underline">{contract.waitsFor.title}</Link>, which has not been sent. Send that one: this goes with it.
            </Banner>
          )}
          {contract.waitsFor && !['draft', 'completed'].includes(contract.waitsFor.status) && (
            <Banner tone="warn">Sending locks this now, but nobody gets it until the client has signed <strong>{contract.waitsFor.title}</strong>. Then it goes out by itself.</Banner>
          )}
          {contract.waitingOnThis?.some((w) => w.status === 'draft') && (
            <Banner tone="ok">
              Sending this also locks {contract.waitingOnThis.filter((w) => w.status === 'draft').map((w) => `"${w.title}"`).join(', ')}. The client signs them in order, in one sitting if they like: each unlocks when the one before it is signed. You countersign them all together at the end. The portal sends {contract.waitingOnThis.filter((w) => w.status === 'draft').length === 1 ? 'it' : 'them'} by itself. Review {contract.waitingOnThis.filter((w) => w.status === 'draft').length === 1 ? 'it' : 'them'} first:{' '}
              {contract.waitingOnThis.filter((w) => w.status === 'draft').map((w, i) => <span key={w.id}>{i ? ', ' : ''}<Link to={`/admin/contracts/${w.id}`} className="underline">{w.title}</Link></span>)}.
            </Banner>
          )}
          <div className="flex flex-wrap gap-3">
            <button disabled={!!busy || !signerSummary.length || contract.waitsFor?.status === 'draft'} onClick={() => run('send', () => adminContractsAPI.send(id), contract.waitsFor && contract.waitsFor.status !== 'completed' ? 'Locked. It goes out by itself once the client has signed the contract before it.' : 'Sent. The first signers have their links.')} className="btn-primary inline-flex items-center gap-2">
              {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            </button>
            <button disabled={!!busy} onClick={async () => { await run('discard', () => adminContractsAPI.discard(id)); navigate('/admin/contracts'); }} className="btn-secondary inline-flex items-center gap-2">
              <Trash2 className="w-4 h-4" /> Discard draft
            </button>
          </div>
        </div>
      )}

      {preview && (
        <div className={card}>
          <div className="flex items-center justify-between"><h2 className="font-medium dark:text-white">Document</h2><button onClick={() => setPreview(null)} className="text-neutral-400"><X className="w-4 h-4" /></button></div>
          <iframe title="Document" src={preview} className="w-full h-[70vh] rounded border border-neutral-200 dark:border-neutral-700 bg-white" />
        </div>
      )}

      {!isDraft && (contract.waitsFor || contract.waitingOnThis?.length > 0) && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Signing order</h2>
          {contract.held_until_parent && (
            <Banner tone="warn">Held: nobody has been sent this yet. It unlocks once the client has signed <strong>{contract.waitsFor?.title}</strong>.</Banner>
          )}
          <ul className="text-sm space-y-1 dark:text-neutral-200">
            {contract.waitsFor && (
              <li>Signed after <Link to={`/admin/contracts/${contract.waitsFor.id}`} className="text-primary-600 hover:underline">{contract.waitsFor.title}</Link> <StatusBadge status={contract.waitsFor.status} held={contract.waitsFor.held} /></li>
            )}
            {contract.waitingOnThis?.map((w) => (
              <li key={w.id}>Then <Link to={`/admin/contracts/${w.id}`} className="text-primary-600 hover:underline">{w.title}</Link> <StatusBadge status={w.status} held={w.held} /></li>
            ))}
          </ul>
        </div>
      )}

      {contract.status === 'signed' && contract.package?.length > 1 && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Countersign the package</h2>
          {contract.package.some((m) => m.status === 'sent') ? (
            <Banner tone="warn">
              The client is still signing {contract.package.filter((m) => m.status === 'sent').map((m) => `"${m.title}"`).join(', ')}. You will be emailed when they have signed everything, and countersign it all at once.
            </Banner>
          ) : (
            <>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                {contract.company_name || 'The client'} has signed every document in the package. One signature countersigns them all: {contract.package.filter((m) => m.status === 'signed').map((m) => m.title).join(', ')}. Each is sealed, emailed and filed on its own.
              </p>
              <label className="block text-sm max-w-md">
                <span className="block text-neutral-600 dark:text-neutral-300 mb-1">Your title (optional)</span>
                <input value={signTitle} onChange={(e) => setSignTitle(e.target.value)} className="input w-full" />
              </label>
              <SignatureCapture onChange={onSignature} signerName={[user?.firstName || user?.first_name, user?.lastName || user?.last_name].filter(Boolean).join(' ')} />
              <button
                disabled={!!busy || !signature?.isValid}
                onClick={() => run('countersign', () => adminContractsAPI.countersignPackage(id, {
                  expected: Object.fromEntries(contract.package.filter((m) => m.status === 'signed').map((m) => [m.id, m.sha256])),
                  signatureType: signature.type, signatureData: signature.data,
                  signedName: signature.type === 'typed' ? signature.data : undefined, title: signTitle || undefined,
                }), 'Countersigned. Everyone has been sent the signed copies.')}
                className="btn-primary inline-flex items-center gap-2"
              >
                {busy === 'countersign' && <Loader2 className="w-4 h-4 animate-spin" />} Countersign all {contract.package.filter((m) => m.status === 'signed').length} and complete
              </button>
            </>
          )}
        </div>
      )}

      {contract.status === 'signed' && !(contract.package?.length > 1) && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Countersign</h2>
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Everyone at {contract.company_name || 'the client'} has signed. Countersigning completes the contract: the signed copy is sealed, emailed to everyone and filed in ConnectWise.
          </p>
          <label className="block text-sm max-w-md">
            <span className="block text-neutral-600 dark:text-neutral-300 mb-1">Your title (optional)</span>
            <input value={signTitle} onChange={(e) => setSignTitle(e.target.value)} className="input w-full" />
          </label>
          <SignatureCapture onChange={onSignature} signerName={[user?.firstName || user?.first_name, user?.lastName || user?.last_name].filter(Boolean).join(' ')} />
          <button
            disabled={!!busy || !signature?.isValid}
            onClick={() => run('countersign', () => adminContractsAPI.countersign(id, {
              expectedSha256: contract.original_sha256, signatureType: signature.type, signatureData: signature.data,
              signedName: signature.type === 'typed' ? signature.data : undefined, title: signTitle || undefined,
            }), 'Countersigned. Everyone has been sent the signed copy.')}
            className="btn-primary inline-flex items-center gap-2"
          >
            {busy === 'countersign' && <Loader2 className="w-4 h-4 animate-spin" />} Countersign and complete
          </button>
        </div>
      )}

      {!isDraft && contract.choices?.length > 0 && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Choices</h2>
          <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-[12rem_1fr]">
            {contract.choices.map((c) => (
              <div key={c.key} className="contents">
                <dt className="text-neutral-500">{c.label}</dt>
                <dd className="dark:text-neutral-200">{c.chosen ? <><strong>{c.chosen}</strong> — chosen by {c.chosenBy}, {formatDateTime(c.chosenAt)}</> : <span className="text-neutral-500">Not chosen yet ({c.options.join(' / ')})</span>}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {contract.status === 'completed' && <ContractAgreementCard contract={contract} />}

      {!isDraft && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Signers</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-neutral-500 dark:text-neutral-400">
                <tr><th className="py-2 pr-4 font-medium">Who</th><th className="py-2 pr-4 font-medium">Status</th><th className="py-2 pr-4 font-medium">Evidence</th><th /></tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {contract.signers.map((s) => (
                  <tr key={s.id} className="align-top">
                    <td className="py-3 pr-4">
                      <div className="font-medium dark:text-white">{s.signedName || s.name}{s.role === 'countersigner' ? ' (countersigned)' : ''}</div>
                      <div className="text-xs text-neutral-500">{s.email}{s.title ? ` · ${s.title}` : ''}{s.role === 'signer' ? ` · order ${s.signOrder}` : ''}</div>
                    </td>
                    <td className="py-3 pr-4 dark:text-neutral-200">
                      {s.status === 'signed' ? <span className="text-green-700 dark:text-green-400">Signed {formatDateTime(s.signedAt)}</span>
                        : s.status === 'declined' ? <span className="text-red-700 dark:text-red-400">Declined{s.declineReason ? `: ${s.declineReason}` : ''}</span>
                          : s.linkSentAt ? (s.firstViewedAt ? 'Opened, not signed' : 'Link sent') : 'Waiting for their turn'}
                      {s.lockedUntil && new Date(s.lockedUntil) > new Date() && <div className="text-xs text-red-600">Locked after wrong codes</div>}
                    </td>
                    <td className="py-3 pr-4 text-xs text-neutral-600 dark:text-neutral-300 space-y-0.5">
                      {s.verifiedAt && <div>Identity: {s.verifiedVia === 'portal_login' ? 'signed in to the portal' : 'emailed code'}, {formatDateTime(s.verifiedAt)}</div>}
                      {s.consentAt && <div>Agreed to sign electronically {formatDateTime(s.consentAt)}</div>}
                      {s.ip && <div>From {s.ip}</div>}
                      {s.userAgent && <div className="max-w-xs truncate" title={s.userAgent}>{s.userAgent}</div>}
                      {s.matchesDocument === false && <div className="text-red-600">Signed a different fingerprint</div>}
                    </td>
                    <td className="py-3 text-right">
                      {contract.status === 'sent' && s.role === 'signer' && s.status === 'pending' && s.linkSentAt && !contract.expired && (
                        <button disabled={!!busy} onClick={() => run(`resend-${s.id}`, () => adminContractsAPI.resend(id, s.id), `A new link went to ${s.email}.`)} className="text-xs text-primary-600 hover:underline">
                          Resend link
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">IP addresses and browsers are kept for you and never appear on the copy the client receives.</p>
        </div>
      )}

      {!isDraft && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">Record</h2>
          <dl className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-[12rem_1fr]">
            <dt className="text-neutral-500">Document fingerprint</dt><dd className="font-mono text-xs break-all dark:text-neutral-200">SHA-256 {contract.original_sha256}</dd>
            <dt className="text-neutral-500">Sent</dt><dd className="dark:text-neutral-200">{contract.times?.sentAt || formatDateTime(contract.sent_at)}</dd>
            {contract.expires_at && contract.status === 'sent' && (<><dt className="text-neutral-500">Link expires</dt><dd className="dark:text-neutral-200">{formatDateTime(contract.expires_at)}</dd></>)}
            {contract.completed_at && (<><dt className="text-neutral-500">Completed</dt><dd className="dark:text-neutral-200">{contract.times?.completedAt || formatDateTime(contract.completed_at)}</dd></>)}
            {contract.final_sha256 && (<><dt className="text-neutral-500">Signed copy fingerprint</dt><dd className="font-mono text-xs break-all dark:text-neutral-200">SHA-256 {contract.final_sha256}</dd></>)}
            {contract.void_reason && (<><dt className="text-neutral-500">Voided</dt><dd className="dark:text-neutral-200">{contract.void_reason}</dd></>)}
            {contract.status === 'completed' && (
              <>
                <dt className="text-neutral-500">ConnectWise</dt>
                <dd className="dark:text-neutral-200">
                  {contract.cw_filed_at
                    ? `Attached to ${contract.cw_record_type} #${contract.cw_record_id}, ${formatDateTime(contract.cw_filed_at)}`
                    : (
                      <span className="inline-flex flex-wrap items-center gap-2">
                        <span className="text-red-700 dark:text-red-400">Not filed{contract.cw_file_error ? `: ${contract.cw_file_error}` : ''}</span>
                        <button disabled={!!busy} onClick={() => run('file', () => adminContractsAPI.file(id), 'Filed in ConnectWise.')} className="text-xs text-primary-600 hover:underline inline-flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" /> Try again
                        </button>
                      </span>
                    )}
                </dd>
              </>
            )}
          </dl>
          <div className="flex flex-wrap gap-3 pt-2">
            <button disabled={!!busy} onClick={async () => { const r = await run('verify', () => adminContractsAPI.verify(id)); if (r) setVerifyResult(r.data); }} className="btn-secondary inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Verify nothing has changed
            </button>
            {['sent', 'signed', 'declined', 'voided'].includes(contract.status) && contract.status !== 'voided' && (
              <button disabled={!!busy} onClick={async () => { const r = await run('version', () => adminContractsAPI.newVersion(id)); if (r) navigate(`/admin/contracts/${r.data.id}`); }} className="btn-secondary">
                Make a new version
              </button>
            )}
            {['sent', 'signed'].includes(contract.status) && <button onClick={() => setShowVoid(true)} className="btn-secondary inline-flex items-center gap-2"><XCircle className="w-4 h-4" /> Void</button>}
          </div>
          {verifyResult && (
            <Banner tone={verifyResult.verified ? 'ok' : 'error'}>
              {verifyResult.verified
                ? 'Verified: the stored document matches its fingerprint, every signature was made on it, and the signed copy is unchanged.'
                : `Not verified: document ${verifyResult.original}, signed copy ${verifyResult.sealed}${verifyResult.signatures.some((s) => !s.ok) ? ', a signature does not match' : ''}.`}
            </Banner>
          )}
          {showVoid && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 space-y-3">
              <p className="text-sm dark:text-neutral-200">Voiding withdraws it: the links stop working and the signers are emailed your reason.</p>
              <input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="Reason" className="input w-full max-w-lg" />
              <div className="flex gap-3">
                <button disabled={!voidReason.trim() || !!busy} onClick={async () => { await run('void', () => adminContractsAPI.void(id, voidReason), 'Voided.'); setShowVoid(false); }} className="btn-danger">Void it</button>
                <button onClick={() => setShowVoid(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isDraft && (
        <div className={card}>
          <h2 className="text-lg font-semibold dark:text-white">History</h2>
          <ol className="space-y-2 text-sm">
            {contract.events.map((e) => {
              const signer = contract.signers.find((s) => s.id === e.signer_id);
              return (
                <li key={e.id} className="flex flex-wrap gap-x-3 text-neutral-700 dark:text-neutral-300">
                  <span className="tabular-nums text-neutral-500 w-44 flex-shrink-0">{formatDateTime(e.created_at)}</span>
                  <span className="flex items-center gap-1">
                    {['signed', 'countersigned', 'completed', 'filed_cw'].includes(e.event) && <CheckCircle className="w-3.5 h-3.5 text-green-600" />}
                    {EVENT_LABELS[e.event] || e.event}
                    {signer ? ` — ${signer.name}` : e.actor ? ` — ${e.actor}` : ''}
                    {e.detail?.to ? ` (${e.detail.to})` : ''}
                    {e.detail?.error ? `: ${e.detail.error}` : ''}
                    {e.detail?.reason ? `: ${e.detail.reason}` : ''}
                  </span>
                  {e.ip && <span className="text-xs text-neutral-400">{e.ip}</span>}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
